import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Star, Quote } from 'lucide-react';

const benefits = [
  'Get a personalized career roadmap in AI, Data & Cloud',
  'Understand which certifications match your goals',
  'Learn about industry hiring trends & salary benchmarks',
  'Completely free — no strings attached',
];

const testimonials = [
  { name: 'Rahul M.', role: 'AI Engineer, Bangalore', text: 'The consultation helped me identify the right path into AI. Within 6 months, I landed my dream role.' },
  { name: 'Priya S.', role: 'Data Analyst, Hyderabad', text: 'I was confused between Data Science and Data Engineering. CloudXAI gave me clarity and a structured plan.' },
  { name: 'Amit K.', role: 'Cloud Architect, Pune', text: 'Their guidance on cloud certifications was spot-on. Highly recommend the free consultation to anyone starting out.' },
];

const BookConsultationPage = () => {
  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-[#0B0F1A] to-[#020617]">
        <div className="absolute top-20 right-1/4 w-80 h-80 bg-[#00E5FF] rounded-full blur-[150px] opacity-10"></div>
        <div className="absolute bottom-10 left-1/4 w-64 h-64 bg-[#1DE9B6] rounded-full blur-[150px] opacity-10"></div>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#1DE9B6] animate-pulse"></span>
            <span className="text-[#00E5FF] text-sm font-medium">Limited Slots Available</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 font-['Space_Grotesk']" data-testid="consultation-heading">
            Book Your Free <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Career Consultation</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto mb-10">
            Not sure where to start in AI, Data, or Cloud? Talk to our career experts — absolutely free.
          </p>

          {/* Benefits */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left mb-12">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#111827]/80 rounded-xl px-4 py-3 border border-[#00E5FF]/10">
                <CheckCircle2 className="w-5 h-5 text-[#1DE9B6] flex-shrink-0 mt-0.5" />
                <span className="text-[#E5E7EB] text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zoho Meeting Embed */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-testid="meeting-embed-section">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-[#111827] border-2 border-[#00E5FF]/30 shadow-2xl shadow-[#00E5FF]/10 overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] px-6 py-4">
                <h2 className="text-[#0B0F1A] font-bold text-lg font-['Space_Grotesk']">Schedule Your Session</h2>
                <p className="text-[#0B0F1A]/70 text-sm">Pick a time that works for you</p>
              </div>
              <div className="p-4 flex justify-center bg-[#0B0F1A]">
                <iframe
                  title="Book a Consultation - CloudXAI"
                  width="100%"
                  height="500"
                  src="https://meeting.zoho.in/meeting/login/embedmeeting.jsp?meetingKey=1375774153&newWindow=true&t=b8480a446e03093a9de81c25b9af62758c53c99afb7e96087a78c4392df5d16e"
                  frameBorder="0"
                  className="rounded-lg w-full max-w-[550px]"
                  data-testid="zoho-meeting-iframe"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 font-['Space_Grotesk']">
              What Our <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Students Say</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-[#0B0F1A] border-2 border-[#00E5FF]/15 hover:border-[#00E5FF]/40 transition-all duration-300">
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-[#00E5FF]/30 mb-4" />
                  <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6">{t.text}</p>
                  <div className="flex items-center gap-3 border-t border-[#00E5FF]/10 pt-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center text-[#0B0F1A] font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{t.name}</p>
                      <p className="text-[#9CA3AF] text-xs">{t.role}</p>
                    </div>
                    <div className="ml-auto flex gap-0.5">
                      {[...Array(5)].map((_, si) => (
                        <Star key={si} className="w-3.5 h-3.5 fill-[#00E5FF] text-[#00E5FF]" />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BookConsultationPage;
